<template>
  <div class="bg-black text-white min-h-screen py-4 sm:py-6 md:py-8 px-4 sm:px-6">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-6 sm:mb-8 border-b-2 border-yellow-500 pb-6">
        <h1 class="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-3 text-yellow-500">TORNEO REGIONAL</h1>
        <p class="text-lg sm:text-2xl font-bold text-orange-400">🥋 TATAMI {{ tatami }}</p>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <p class="text-xl text-yellow-400 font-bold">⏳ Cargando datos...</p>
        <p class="text-sm text-gray-400 mt-2">Tornéo ID: {{ torneoId }} | Tatami: {{ tatami }}</p>
      </div>

      <!-- Combate Actual -->
      <div v-else-if="combateActual" class="flex items-center justify-center">
        <div class="w-full max-w-6xl">
          <!-- Categoría y Llave -->
          <div class="text-center mb-12 pb-8 border-b-4 border-yellow-500">
            <p class="text-2xl sm:text-4xl md:text-5xl font-bold text-yellow-500 mb-2">{{ combateActual.categoria_nombre }}</p>
            <p class="text-sm sm:text-2xl text-yellow-300">{{ combateActual.llave_identificador }}</p>
          </div>

          <!-- Combate Normal -->
          <div v-if="!combateActual.es_bye">
            <!-- Arena de Combate -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8 items-center mb-12">

              <!-- Competidor 1 (Blanco/Izquierda) -->
              <div class="flex flex-col items-center">
                <div class="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 bg-white rounded-full flex items-center justify-center border-4 border-gray-600 shadow-2xl mb-6 overflow-hidden">
                  <img
                    v-if="getFotoUrl(combateActual.competidor1_foto_url) && !fotoErrorBlanco"
                    :src="getFotoUrl(combateActual.competidor1_foto_url)"
                    class="w-full h-full object-cover"
                    @error="fotoErrorBlanco = true"
                  />
                  <span v-else class="text-8xl sm:text-9xl md:text-9xl">⚪</span>
                </div>
                <div class="bg-gray-200 w-full p-6 rounded-xl border-4 border-gray-600 text-center">
                  <p class="text-2xl sm:text-3xl md:text-4xl font-black text-black uppercase tracking-wider">
                    {{ combateActual.competidor1_apelido }}
                  </p>
                  <p v-if="combateActual.competidor1_regional" class="text-sm sm:text-base md:text-lg font-bold text-gray-700 uppercase mt-1">
                    {{ combateActual.competidor1_regional }}
                  </p>
                </div>
                <div class="mt-6 flex justify-center gap-6">
                  <span
                    v-for="r in resumenRounds"
                    :key="r.numero"
                    class="w-16 h-16 sm:w-16 sm:h-16 md:w-16 md:h-16 rounded-full border-4 transition-colors"
                    :class="claseDot('blanco', r.resultado)"
                  ></span>
                </div>
              </div>

              <!-- Centro: VS y Estado -->
              <div class="flex flex-col items-center justify-center gap-6">
                <!-- VS -->
                <div class="text-center">
                  <p class="text-4xl sm:text-5xl md:text-7xl font-black text-yellow-400">VS</p>
                  <div class="mt-4 h-1 w-16 bg-white-500 mx-auto"></div>
                </div>

                <!-- Estado del Combate -->
                <div class="bg-blue-700 w-full sm:w-48 p-4 rounded-lg border-3 border-blue-500 text-center">
                  <p class="text-xs sm:text-sm text-blue-200 mb-1">ESTADO</p>
                  <p class="text-lg sm:text-2xl font-bold text-white uppercase">
                    {{ combateActual.estado?.toUpperCase() || 'PENDIENTE' }}
                  </p>
                </div>

                <!-- Ganador (debajo del estado, mismo diseño de antes) -->
                <div v-if="combateActual.ganador_apelido" class="w-full sm:w-48 p-4 bg-gradient-to-r from-green-600 to-green-700 rounded-2xl border-4 border-green-500 text-center shadow-2xl">
                  <p class="text-sm sm:text-base font-black text-white uppercase mb-1">🏆 GANADOR 🏆</p>
                  <p class="text-lg sm:text-2xl font-black text-white uppercase">{{ combateActual.ganador_apelido }}</p>
                </div>

                <!-- Reloj -->
                <div class="text-yellow-400 text-sm">⏱️ {{ ultimaActualizacion }}</div>
              </div>

              <!-- Competidor 2 (Negro/Derecha) -->
              <div class="flex flex-col items-center">
                <div class="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 bg-gray-900 rounded-full flex items-center justify-center border-4 border-gray-600 shadow-2xl mb-6 overflow-hidden">
                  <img
                    v-if="getFotoUrl(combateActual.competidor2_foto_url) && !fotoErrorNegro"
                    :src="getFotoUrl(combateActual.competidor2_foto_url)"
                    class="w-full h-full object-cover"
                    @error="fotoErrorNegro = true"
                  />
                  <span v-else class="text-8xl sm:text-9xl md:text-9xl">⚫</span>
                </div>
                <div class="bg-gray-900 w-full p-6 rounded-xl border-4 border-gray-600 text-center">
                  <p class="text-2xl sm:text-3xl md:text-4xl font-black text-yellow-400 uppercase tracking-wider">
                    {{ combateActual.competidor2_apelido }}
                  </p>
                  <p v-if="combateActual.competidor2_regional" class="text-sm sm:text-base md:text-lg font-bold text-yellow-200 uppercase mt-1">
                    {{ combateActual.competidor2_regional }}
                  </p>
                </div>
                <div class="mt-6 flex justify-center gap-6">
                  <span
                    v-for="r in resumenRounds"
                    :key="r.numero"
                    class="w-16 h-16 sm:w-16 sm:h-16 md:w-16 md:h-16 rounded-full border-4 transition-colors"
                    :class="claseDot('negro', r.resultado)"
                  ></span>
                </div>
              </div>
            </div>

            <!-- Mensaje de espera de confirmación -->
            <p v-if="combateActual.ganador_apelido" class="text-center text-sm sm:text-base text-green-400 mt-4">
              ⏳ Esperando confirmación de los jueces para el siguiente combate...
            </p>
          </div>

          <!-- BYE (no debería aparecer en un tatami, pero por si acaso) -->
          <div v-else class="text-center py-12">
            <div class="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 bg-yellow-600 rounded-full flex items-center justify-center border-4 border-yellow-500 shadow-2xl mx-auto mb-8 overflow-hidden">
              <img
                v-if="getFotoUrl(combateActual.competidor1_foto_url) && !fotoErrorBlanco"
                :src="getFotoUrl(combateActual.competidor1_foto_url)"
                class="w-full h-full object-cover"
                @error="fotoErrorBlanco = true"
              />
              <span v-else class="text-9xl md:text-9xl">⚪</span>
            </div>
            <p class="text-4xl sm:text-5xl md:text-6xl font-black text-yellow-500 mb-2 uppercase">
              {{ combateActual.competidor1_apelido }}
            </p>
            <p class="text-xl sm:text-2xl md:text-3xl font-bold text-yellow-400 uppercase tracking-wide">
              Avanza automáticamente
            </p>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-12">
        <p class="text-2xl text-yellow-500 font-bold mb-4">⏳ Sin combates pendientes en este tatami</p>
        <p class="text-sm text-gray-400 mt-4">Recargando automáticamente...</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAPI } from '../../composables/useAPI'
import { getFotoUrl } from '../../config'

const route = useRoute()
const api = useAPI()

const combateActual = ref(null)
const combates = ref([])
const loading = ref(false)
const ultimaActualizacion = ref('')
// Si la foto de un competidor falla al cargar, se vuelve a mostrar el emoji de respaldo
const fotoErrorBlanco = ref(false)
const fotoErrorNegro = ref(false)
const torneoId = ref(null)
const tatami = ref(null)
let refreshInterval = null

// Al cambiar de combate, reintentar mostrar las fotos (por si la anterior falló)
watch(() => combateActual.value?.id, () => {
  fotoErrorBlanco.value = false
  fotoErrorNegro.value = false
})

// Resultado de cada round del combate actual: 'blanco', 'negro', 'empate' o null (sin decidir aún)
const resumenRounds = computed(() => {
  if (!combateActual.value) return []
  const rounds = combateActual.value.rounds || []
  return rounds.map(round => ({
    numero: round.numero_round,
    resultado: round.ganador_color
  }))
})

// Punto (dot) por round para cada competidor: solo borde si no está decidido,
// verde si lo ganó, rojo si lo perdió, amarillo si fue empate
const claseDot = (miColor, resultado) => {
  if (!resultado) return 'border-gray-400 bg-transparent'
  if (resultado === 'empate') return 'border-yellow-400 bg-yellow-400'
  if (resultado === miColor) return 'border-green-500 bg-green-500'
  return 'border-red-500 bg-red-500'
}

const actualizarHora = () => {
  const now = new Date()
  ultimaActualizacion.value = now.toLocaleTimeString('es-CO')
}

const extraerCombateActual = () => {
  combateActual.value = combates.value.find(l => l.estado === 'pendiente' || l.estado === 'en_curso') || null
}

// Actualiza los datos del combate actual (votos, estado) sin cambiar de combate
// hasta que los 3 jueces hayan confirmado el paso al siguiente.
const actualizarDatos = async () => {
  try {
    const respuesta = await api.getCombateActualTatami(torneoId.value, tatami.value)
    combates.value = respuesta.llaves || []

    // Si aún no hay combate actual, extraer el primero disponible
    if (!combateActual.value) {
      extraerCombateActual()
      actualizarHora()
      return
    }

    // Refrescar el combate actual con datos frescos
    const actualizado = combates.value.find(l => l.id === combateActual.value.id)
    if (actualizado) {
      combateActual.value = actualizado
    }

    // Si el combate actual terminó, solo avanzar al siguiente cuando los 3 jueces confirmaron
    if (combateActual.value?.estado === 'finalizado') {
      const confirmaciones = await api.verificarConfirmaciones(combateActual.value.id)
      if (confirmaciones.todos_confirmaron) {
        extraerCombateActual()
      }
    }

    actualizarHora()
  } catch (err) {
    console.error('Error actualizando tatami:', err)
  }
}

const cargarInicial = async () => {
  if (!torneoId.value || !tatami.value) return

  try {
    loading.value = true
    const respuesta = await api.getCombateActualTatami(torneoId.value, tatami.value)
    combates.value = respuesta.llaves || []
    extraerCombateActual()
    actualizarHora()
  } catch (err) {
    console.error('Error cargando tatami:', err)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  torneoId.value = parseInt(route.params.torneoId)
  tatami.value = parseInt(route.params.tatami)

  await cargarInicial()

  // Polling cada 4 segundos
  refreshInterval = setInterval(actualizarDatos, 4000)

  // Entrar en modo fullscreen automáticamente
  setTimeout(() => {
    const elem = document.documentElement
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch(err => console.log('Fullscreen no disponible:', err))
    }
  }, 500)
})

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval)
})
</script>
